import type { RoomView } from "@shared/types";
import { motion } from "motion/react";
import { useEffect, useRef } from "react";
import { Avatar } from "../../components/Avatar";
import { Check, X } from "../../components/Icon";
import { TimerBar } from "../../components/Timer";
import { useI18n } from "../../i18n";
import type { RoomClient } from "../../lib/room-client";
import { haptic, play } from "../../lib/sfx";
import { AudienceNote, Banner, FinalResults, GameChrome, PlayerStrip, Scoreboard } from "../common/GameKit";
import { useGameAct } from "../common/useRoom";

export interface TrufV {
  phase: "intro" | "question" | "reveal" | "over";
  deadline: number | null;
  phaseMs: number;
  i: number;
  total: number;
  statement: string | null;
  cat: string;
  truth: boolean | null;
  explanation: string | null;
  myAnswer: boolean | null;
  answered: string[];
  answers: Record<string, boolean> | null;
  scores: Record<string, number>;
  gained: Record<string, number>;
  streak: Record<string, number>;
  best: Record<string, number>;
  correct: Record<string, number>;
}

export function TrufView({ client, view, tv }: { client: RoomClient; view: RoomView; tv?: boolean }) {
  const v = view.view as TrufV;
  const { d, f } = useI18n();
  const g = useGameAct(client);
  const me = view.me?.id ?? "";
  const players = view.players.filter((p) => p.role === "player");
  const isPlayer = view.me?.role === "player" && !tv;

  const revealedFor = useRef(-1);
  useEffect(() => {
    if (v.phase !== "reveal" || revealedFor.current === v.i) return;
    revealedFor.current = v.i;
    const mine = v.answers?.[me];
    if (mine === undefined) play("reveal", { volume: 0.6 });
    else if (mine === v.truth) {
      play("correct");
      if ((v.streak[me] ?? 0) >= 3) play("streak", { delay: 0.3 });
    } else {
      play("buzz");
      haptic([30, 50, 30]);
    }
  }, [v.phase, v.i]); // eslint-disable-line react-hooks/exhaustive-deps

  const skip = v.phase === "question" || v.phase === "reveal" || v.phase === "intro" ? () => g({ t: "next" }) : undefined;

  if (v.phase === "over") {
    return (
      <>
        <GameChrome client={client} view={view} />
        <FinalResults
          client={client}
          view={view}
          title={d.truf.final}
          rows={players.map((p) => ({ id: p.id, score: v.scores[p.id] ?? 0, sub: f(d.truf.accuracy, { a: v.correct[p.id] ?? 0, b: v.total }) }))}
        />
      </>
    );
  }

  return (
    <>
      <GameChrome client={client} view={view} onSkip={skip} />
      <div className="shell mx-auto max-w-3xl space-y-6 pt-2">
        <p className="text-center font-extrabold text-ink-3">
          {f(d.truf.statement, { n: v.i + 1, total: v.total })} · {(d.cats as Record<string, string>)[v.cat] ?? v.cat}
        </p>
        {v.phase === "intro" && <Banner title={d.games.truf.name} sub={d.games.truf.tagline} color="var(--color-truf)" />}
        {v.phase !== "intro" && (
          <motion.div key={v.i} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="surface p-6 text-center sm:p-10">
            <p className="text-[clamp(24px,4.4vw,42px)] leading-tight font-extrabold">{v.statement}</p>
          </motion.div>
        )}
        {v.phase === "question" && (
          <>
            <TimerBar deadline={v.deadline} total={v.phaseMs} offset={client.offset()} paused={view.paused} color="var(--color-truf)" tickLast={3} big />
            {isPlayer ? (
              <div className="grid grid-cols-2 gap-4">
                <Buzzer kind="true" chosen={v.myAnswer} disabled={v.myAnswer !== null} onPick={() => g({ t: "answer", v: true })} />
                <Buzzer kind="false" chosen={v.myAnswer} disabled={v.myAnswer !== null} onPick={() => g({ t: "answer", v: false })} />
              </div>
            ) : (
              <AudienceNote />
            )}
            <p className="text-center font-bold text-ink-3">{f(d.truf.answered, { a: v.answered.length, b: players.length })}</p>
            <PlayerStrip players={players} done={v.answered} />
          </>
        )}
        {v.phase === "reveal" && <Reveal view={view} v={v} me={me} />}
      </div>
    </>
  );
}

function Buzzer({ kind, chosen, disabled, onPick }: { kind: "true" | "false"; chosen: boolean | null; disabled: boolean; onPick(): void }) {
  const { d } = useI18n();
  const isTrue = kind === "true";
  const me = chosen === isTrue;
  const color = isTrue ? "var(--color-truf)" : "var(--color-candy)";
  const deep = isTrue ? "var(--color-truf-deep)" : "var(--color-candy-deep)";
  return (
    <button
      className="relative flex aspect-square flex-col items-center justify-center gap-2 rounded-[36px] text-center transition-transform"
      style={{
        background: color,
        boxShadow: me || disabled ? `0 2px 0 ${deep}` : `0 12px 0 ${deep}, inset 0 4px 0 rgba(255,255,255,.3)`,
        transform: me || disabled ? "translateY(10px)" : undefined,
        opacity: disabled && !me ? 0.4 : 1,
        color: isTrue ? "#00243a" : "#fff",
      }}
      disabled={disabled}
      onClick={() => {
        play("pop");
        haptic(18);
        onPick();
      }}
    >
      {isTrue ? <Check size={72} strokeWidth={3.2} /> : <X size={72} strokeWidth={3.2} />}
      <span className="display text-2xl sm:text-3xl">{isTrue ? d.truf.true : d.truf.false}</span>
    </button>
  );
}

function Reveal({ view, v, me }: { view: RoomView; v: TrufV; me: string }) {
  const { d, f } = useI18n();
  const mine = v.answers?.[me];
  const gained = v.gained[me] ?? 0;
  const saidTrue = view.players.filter((p) => v.answers?.[p.id] === true);
  const saidFalse = view.players.filter((p) => v.answers?.[p.id] === false);
  const players = view.players.filter((p) => p.role === "player");
  return (
    <div className="space-y-6">
      <motion.p
        initial={{ scale: 0.4, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 320, damping: 14 }}
        className="display text-center text-[clamp(40px,9vw,88px)]"
        style={{ color: v.truth ? "var(--color-truf)" : "var(--color-candy)" }}
      >
        {v.truth ? d.truf.itsTrue : d.truf.itsFalse}
      </motion.p>
      {v.explanation && <p className="text-center text-xl text-ink-2">{v.explanation}</p>}
      {view.me?.role === "player" && (
        <p className="text-center text-2xl font-black" style={{ color: mine === undefined ? "var(--color-ink-3)" : mine === v.truth ? "var(--color-good)" : "var(--color-candy)" }}>
          {mine === undefined ? d.truf.noAnswer : mine === v.truth ? f(d.truf.correct, { n: gained }) : d.truf.wrong}
          {(v.streak[me] ?? 0) >= 3 && <span className="ml-2 text-flame">{f(d.truf.streak, { n: v.streak[me] })}</span>}
        </p>
      )}
      <div className="grid grid-cols-2 gap-4">
        {[
          { list: saidTrue, label: d.truf.true, ok: v.truth === true, color: "var(--color-truf)" },
          { list: saidFalse, label: d.truf.false, ok: v.truth === false, color: "var(--color-candy)" },
        ].map((col) => (
          <div key={col.label} className="surface p-4">
            <p className="mb-3 text-center font-black" style={{ color: col.color }}>{col.label}</p>
            <div className="flex flex-wrap justify-center gap-2">
              {col.list.map((p) => (
                <Avatar key={p.id} index={p.avatar} color={p.color} size={44} mood={col.ok ? "happy" : "sad"} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <Scoreboard view={view} rows={players.map((p) => ({ id: p.id, score: v.scores[p.id] ?? 0, delta: v.gained[p.id] || undefined }))} />
    </div>
  );
}
